import { UNIT_IDS } from "types/units";

import { useShapefileBufferUnitStore } from "./shapefileBufferUnitStore";
import {
  ShapefileBufferLabelUnit,
  ShapefileBufferLargerUnit,
  shapefileBufferLabelUnit
} from "./shapefileFilterPredicate";

/**
 * Which unit a shapefile buffer is typed in and read back in. One question,
 * asked by two places that must never answer it differently: the buffer
 * control's own unit switch, and the label the chip and the filter tree spell
 * the applied buffer with. While the control resolved it privately, a switch
 * left on metres typed a distance in metres and the chip beside it read the
 * same distance out in kilometres.
 */

/**
 * The two units a distance can be typed in, one pair per system of
 * measurement: whoever reads lengths in feet is offered miles and feet, and
 * everybody else kilometres and metres. Two are enough for a buffer, and the
 * larger of each pair comes first — it is the one the switch starts on, since
 * a buffer is usually asked for in whole miles or kilometres.
 */
const UNIT_PAIR: Record<
  ShapefileBufferLargerUnit,
  ReadonlyArray<ShapefileBufferLabelUnit>
> = {
  [UNIT_IDS.KILOMETERS]: [UNIT_IDS.KILOMETERS, UNIT_IDS.METERS],
  [UNIT_IDS.MILES]: [UNIT_IDS.MILES, UNIT_IDS.FEET]
};

/**
 * The pair on offer to whoever reads lengths in this unit, larger one first.
 * Which system of measurement that is comes from the same place the label's
 * fallback does, so the switch and the label are never offering and spelling
 * different halves of the screen.
 */
export function shapefileBufferUnitPair(
  displayUnit: Maybe<string>
): ReadonlyArray<ShapefileBufferLabelUnit> {
  return UNIT_PAIR[shapefileBufferLabelUnit(displayUnit)];
}

/**
 * The unit the switch was last set to, when it is still one a buffer can be
 * said in. A plain read, for the one caller that only needs a starting value —
 * anything that has to follow a flip subscribes to the store instead
 * (`useShapefileBufferLabelUnit`). Whether the unit is one of the two on offer
 * is a separate question, settled against the pair being shown.
 */
export function storedShapefileBufferUnit(): Maybe<string> {
  return useShapefileBufferUnitStore.getState().remembered;
}

/**
 * The unit a buffer is typed in and read back in right now: the remembered
 * choice while it is one of the two on offer, and the larger of the pair
 * otherwise. A choice counts only within its own pair — somebody now reading
 * imperial is not typing kilometres any more — and that is also what happens
 * to a unit remembered from a build that offered more of them.
 */
export function resolveShapefileBufferUnit(
  displayUnit: Maybe<string>,
  stored: Maybe<string>
): ShapefileBufferLabelUnit {
  const pair = shapefileBufferUnitPair(displayUnit);
  return stored && pair.includes(stored as ShapefileBufferLabelUnit)
    ? (stored as ShapefileBufferLabelUnit)
    : pair[0];
}
